import type { WidgetConfig } from '../schemas';
import { TrendIndicator } from '../../../components/ui/TrendIndicator';
import { useFieldCatalog, metricLabel, metricFormat, metricGoodDirection } from '../fieldCatalog';
import { formatMetricValue } from '../formatValue';
import { aggregateMetric } from '../aggregate';
import { computeCompare } from '../compareFormat';

type Row = Record<string, string | number>;

// Tables de correspondance vers des classes Tailwind LITTÉRALES : jamais de
// `text-${x}` par template, sinon la purge Tailwind les supprime du build.
const FONT: Record<string, string> = { sans: 'font-sans', serif: 'font-serif', mono: 'font-mono' };
const ALIGN: Record<string, string> = { left: 'text-left', center: 'text-center', right: 'text-right' };
const TITLE_SIZE: Record<string, string> = { xs: 'text-xs', sm: 'text-sm', base: 'text-base' };
const VALUE_SIZE: Record<string, string> = {
  xl: 'text-xl',
  '2xl': 'text-2xl',
  '3xl': 'text-3xl',
  '4xl': 'text-4xl',
  '5xl': 'text-5xl',
};

interface WidgetProps {
  config: WidgetConfig;
  data: { current: Row[]; previous: Row[] | null; meta: any } | null;
  loading?: boolean;
  error?: string;
}

export default function MetricCardWidget({ config, data, loading, error }: WidgetProps) {
  // Hook avant tout early return (règles des hooks) : le catalogue fournit le libellé FR,
  // le format d'affichage et le type (additive/ratio) de la métrique.
  const catalog = useFieldCatalog(data?.meta?.source_type as string | undefined);

  if (loading) {
    return (
      <div className="h-full">
        <div className="h-3 w-1/2 bg-[#F4F5FA] rounded animate-pulse mb-3" />
        <div className="h-7 w-2/3 bg-[#F4F5FA] rounded animate-pulse" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full flex items-center">
        <span className="text-sm text-red-600">{error}</span>
      </div>
    );
  }

  const metric = config.metrics?.[0];

  if (!data || !config.data_source_id || !metric) {
    return (
      <div className="h-full flex items-center justify-center">
        <span className="text-sm text-[#8B8FA7]">À configurer</span>
      </div>
    );
  }

  const def = catalog?.metrics.find((m) => m.key === metric);
  const format = metricFormat(catalog, metric);

  const currentValue = aggregateMetric(data.current, metric, def);
  const previousValue = data.previous ? aggregateMetric(data.previous, metric, def) : null;

  // La comparaison peut venir du rapport (période) ou du widget : dans les deux cas le
  // backend ne renvoie `previous` que si elle est demandée. Sa présence suffit donc —
  // exiger `config.compare` masquait la variation quand la comparaison venait du rapport.
  // Le format de comparaison par métrique pilote le rendu du delta (défaut 'percent').
  const compareFormat = config.metric_compare_format?.[metric] ?? 'percent';
  const compare = computeCompare(currentValue, previousValue, compareFormat);

  // Style (StyleTab). Valeurs par défaut = rendu historique du KpiCard frameless.
  const d = config.display ?? {};
  const fontClass = FONT[d.font_family ?? 'sans'] ?? FONT.sans;
  const alignTitle = ALIGN[d.align_title ?? 'left'] ?? ALIGN.left;
  const alignValue = ALIGN[d.align_value ?? 'left'] ?? ALIGN.left;
  const titleSize = TITLE_SIZE[d.title_size ?? 'sm'] ?? TITLE_SIZE.sm;
  const valueSize = VALUE_SIZE[d.value_size ?? '2xl'] ?? VALUE_SIZE['2xl'];
  const titleColor = d.title_color ?? '#8B8FA7';
  const valueColor = (config.format?.color as string | undefined) ?? '#1B2559';

  // Réglage explicite du widget (trend_is_good) prioritaire ; à défaut, le catalogue
  // (ou l'override par-métrique) prend le relais à la place du 'up' en dur.
  const good = d.trend_is_good ?? metricGoodDirection(catalog, metric, config.metric_good_direction?.[metric]);

  const titleClass = [
    titleSize,
    'font-medium',
    alignTitle,
    d.title_italic ? 'italic' : '',
    d.title_underline ? 'underline' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={`h-full ${fontClass}`}>
      <div className={`${titleClass} mb-3`} style={{ color: titleColor }}>
        {config.title || metricLabel(catalog, metric, config.metric_labels)}
      </div>
      <p className={`${valueSize} font-bold font-mono leading-none ${alignValue}`} style={{ color: valueColor }}>
        {formatMetricValue(currentValue, format)}
      </p>
      {compare.showDelta && compare.kind === 'percent' && compare.percent !== null && (
        <div className={`mt-2 ${alignValue}`}>
          <TrendIndicator value={compare.percent} goodDirection={good} />
        </div>
      )}
      {compare.showDelta && compare.kind === 'diff' && compare.diff !== null && (
        <div className={`mt-2 ${alignValue}`}>
          <TrendIndicator
            value={compare.diff}
            formattedValue={`${compare.diff > 0 ? '+' : ''}${formatMetricValue(compare.diff, format)}`}
            goodDirection={good}
          />
        </div>
      )}
      {compare.showDelta && compare.kind === 'both' && compare.diff !== null && (
        <div className={`mt-2 ${alignValue}`}>
          <TrendIndicator
            value={compare.percent ?? compare.diff}
            formattedValue={`${compare.diff > 0 ? '+' : ''}${formatMetricValue(compare.diff, format)}${
              compare.percent !== null ? ` (${compare.percent > 0 ? '+' : ''}${compare.percent}%)` : ''
            }`}
            goodDirection={good}
          />
        </div>
      )}
    </div>
  );
}
